import { type ChangeEvent, type FormEvent, useEffect, useRef } from 'react'
import cn from 'clsx'
import { SearchIcon, VisuallyHidden } from '@ds/components'
import styles from './SearchBar.module.css'

/** Both search icons are 20px here; the DS default of 24 belongs to the actions row. */
const ICON_SIZE = 20

type TSearchBarProps = {
    value: string
    searchLabel: string
    submitLabel: string
    isMobileOpen: boolean
    onChange: (value: string) => void
    onSubmit: () => void
    onMobileOpen: () => void
    onMobileClose: () => void
}

export const SearchBar = ({
    value,
    searchLabel,
    submitLabel,
    isMobileOpen,
    onChange,
    onSubmit,
    onMobileOpen,
    onMobileClose,
}: TSearchBarProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const closeCheckRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    /*
     * audio-web mounts the expanded field with `autoFocus`; here the field is always mounted, so the
     * focus follows the state change. It is deferred by a tick because the browser finishes its own
     * post-click focus handling after the effect, and that would take the focus straight back off.
     */
    useEffect(() => {
        if (!isMobileOpen) {
            return undefined
        }

        const focusTimer = setTimeout(() => inputRef.current?.focus(), 0)

        return () => clearTimeout(focusTimer)
    }, [isMobileOpen])

    /*
     * The expanded field has no close control: it collapses when the focus leaves it, as in audio-web.
     * A native `focusout` listener rather than the `onBlur` prop, which the compat layer delivers for
     * focus changes anywhere on the page — every one of those read as "the field lost the focus" and
     * closed it in the tick it opened. The verdict is deferred so it reads where the focus settled.
     */
    useEffect(() => {
        const form = formRef.current

        if (!isMobileOpen || !form) {
            return undefined
        }

        const handleFocusOut = () => {
            clearTimeout(closeCheckRef.current)
            closeCheckRef.current = setTimeout(() => {
                const active = document.activeElement

                if (!(active instanceof Node) || !form.contains(active)) {
                    onMobileClose()
                }
            }, 0)
        }

        form.addEventListener('focusout', handleFocusOut)

        return () => {
            form.removeEventListener('focusout', handleFocusOut)
            clearTimeout(closeCheckRef.current)
        }
    }, [isMobileOpen, onMobileClose])

    const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
        onChange(event.currentTarget.value)

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        onSubmit()
    }

    return (
        <div className={styles.search} data-mobile-search-open={isMobileOpen ? '' : undefined}>
            {isMobileOpen ? null : (
                <button type="button" className={styles.mobileToggle} onClick={onMobileOpen}>
                    <SearchIcon width={ICON_SIZE} height={ICON_SIZE} />
                </button>
            )}
            <form
                ref={formRef}
                className={cn(styles.form, isMobileOpen && styles.formOpen)}
                onSubmit={handleSubmit}
            >
                <input
                    ref={inputRef}
                    type="search"
                    className={styles.input}
                    value={value}
                    placeholder={searchLabel}
                    maxLength={50}
                    autoComplete="off"
                    onChange={handleChange}
                />
                <button type="submit" className={styles.submit} aria-label={submitLabel}>
                    <SearchIcon width={ICON_SIZE} height={ICON_SIZE} />
                    <VisuallyHidden>{submitLabel}</VisuallyHidden>
                </button>
            </form>
        </div>
    )
}
